/* **The KANON `raporty-2026-09-17` control vocabulary — one transcription, every surface.**
 *
 * The export's `\3c style>` block (identical in all six files) declares nine primitives, and every
 * screen of this slice is built out of them: `.chip`, `.ibtn`, `.tog`, `.seg`, `.tile`, `.dash`,
 * `.wd`, `.field` and the three buttons. This file is that block, read off `04-edytor.html` and
 * written once.
 *
 * ── Why it exists, and it is a defect report rather than a preference ────────────────────────
 * The canon was transcribed correctly ONCE — `SurveyDetailCard.module.css` is its detail card and
 * `ReviewKindFilter.module.css` is its `.seg` — and then re-drawn from scratch with different
 * numbers in `SurveyEditor.module.css`: a chip at radius 18 against the canon's 14, a segment at
 * `0 48px` against `0 14px`, a 36px icon button where the canon draws 28, a `.wd` at 36 against 32.
 * The owner looked at the running screen on 17 September 2026 and said it plainly: „miało być
 * identycznie wizualnie jak w kierunku 1, a zrobiłeś inaczej". A second re-drawing would produce a
 * third set of numbers, so there is one set and the surfaces read it.
 *
 * ── The three edges ─────────────────────────────────────────────────────────────────────────
 * The canon draws `rgba(255,255,255,.09)` as a separator (`--border`), `.13` on every outlined
 * control (`--border-canon-edge`, added to `tokens/colors.css` for this canon) and `.3` on a
 * control that is ON (`--border-3`). `--border-2` is .20 in the dark theme and is none of them.
 *
 * ── The shared button skins declare NO geometry, so every consumer must ──────────────────────
 * `shared/ui/{ember,ghost}Button.module.css` own the fill, the ink, the edge and the three
 * states and deliberately state no height, no radius, no type. Four controls on this screen wore
 * one of them and declared none of it, which is why they rendered square and at weight 400. The
 * `.ember` / `.ghost` blocks below are those six properties and nothing the skins own —
 * a declaration on both sides is settled by CHUNK ORDER, which differs between the dev server and
 * the build (see `emberButton.module.css` for the measurement that cost).
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

/* ── The chip ──────────────────────────────────────────────────────────────────────────────── */

.chip {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-report-chip);
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-none);
  color: var(--text-2);
  white-space: nowrap;
  background: var(--bg-chip);

  /* Split into three: the value allowlist requires a TOKEN colour inside a `border` shorthand, and
   * the transparent edge is what keeps a filled chip and an outlined one the same height. */
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  border-radius: var(--r-chip);
}

/* `.chip.line` — the outlined cut, used for a FILTER rather than for a fact. The transparent edge
 * above keeps the two the same height; here it is painted. */
.chipLine {
  background: transparent;
  border-color: var(--border-canon-edge);
}

/* The LARGER cut — `06-katalog-pytan.html`'s filter chips, 28 tall at `0 10` and 12 / 600. Same
 * file, later in source order, equal specificity: the override is settled by this stylesheet's own
 * order rather than by the order two chunks happen to land in. */
.chipLg {
  height: var(--h-report-shelf-chip);
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-label);
}

.chipIcon {
  display: block;
  flex: none;
  width: var(--sz-report-chip-icon);
  height: var(--sz-report-chip-icon);
}

/* ── The icon button, at the canon's four sizes ────────────────────────────────────────────── */

.ibtn {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--sz-report-ibtn);
  height: var(--sz-report-ibtn);
  padding: var(--space-0);
  color: var(--text-2);
  background: transparent;
  border: var(--border-hairline) solid var(--border-canon-edge);
  border-radius: var(--radius-mark);
  cursor: pointer;
}

/* Drawn at 28px, which is under the pointer-target floor, so the target is extended past the
 * painted box the way every other small control in this app does. */
.ibtn::after {
  content: '';
  position: absolute;
  inset: var(--hit-extend);
}

.ibtn:hover {
  color: var(--text);
}

.ibtn:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* Focusable while refused — a control carrying a reason has to stay in the tab order for the
 * reason to be reachable at all (view ADR §12). */
.ibtn[aria-disabled='true'],
.ibtn:disabled {
  cursor: default;
  opacity: 0.35;
}

/* The canon's `.ibtn.on`: primary ink and the .3 edge. It marks a control that is currently DOING
 * something — the open menu, the filed bookmark — never merely a hover. */
.ibtnOn {
  color: var(--text);
  border-color: var(--border-3);
}

/* ── A ROW's own acts: the glyph and nothing else ──────────────────────────────────────────────
 * Owner, 17 September 2026: the pencil and the trash on a catalog row sat on „grey filled tiles"
 * and are to be minimal. `.ibtnBare` takes the edge away and keeps everything else `.ibtn` gives —
 * the 28px box, the target extended past it, the hover ink, the refused opacity — so the only box
 * these controls ever draw is the focus ring.
 *
 * `.ibtnDanger` is the trash's ink. `--danger-ink` rather than `--danger`: the raw one is declared
 * for dots, fills and rings and is not the value for a mark that has to be read. The `:hover` rule
 * is not a duplicate of the colour above it — `.ibtn:hover` outranks a single class, so without it
 * the trash turns grey under the pointer. */
.ibtnBare {
  border-color: transparent;
}

.ibtnDanger,
.ibtnDanger:hover {
  color: var(--danger-ink);
}

/* The three larger cuts, each of them a size the canon states rather than a number picked here:
 * 36/r12 in the editor's top bar and the list's toolbar, 40/r13 on the detail card, 44/r13 in the
 * page header. */
.ibtnMd {
  width: var(--sz-report-filter);
  height: var(--sz-report-filter);
  border-radius: var(--radius-12px);
}

.ibtnLg {
  width: var(--sz-report-ibtn-lg);
  height: var(--sz-report-ibtn-lg);
  border-radius: var(--radius-13px);
}

.ibtnIcon {
  display: block;
  width: var(--sz-report-glyph);
  height: var(--sz-report-glyph);
}

.ibtnIconMd {
  display: block;
  width: var(--sz-report-glyph-md);
  height: var(--sz-report-glyph-md);
}

/* ── The switch lives in `shared/ui/Toggle` since 26 September 2026 ─────────────────────────
 * Promoted on its second consumer, the booking settings and the week pattern. */

/* ── The segmented control lives in `shared/ui/segmented.module.css` since 18 September 2026 ──
 * Promoted on its second consumer, the account screen's Aplikacja tab, whose three rows are the
 * same groove at the same numbers. `.seg`, `.segOption`, `.segOptionOn` and `.segIcon` moved
 * unchanged; `CadenceFields` and `NewQuestionCard` read them from there. */

/* ── The tile, the dashed opener, the weekday and the field ────────────────────────────────── */

.tile {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--sz-report-tile);
  height: var(--sz-report-tile);
  color: var(--text-2);
  background: var(--bg-inset);
  border-radius: var(--radius-12px);
}

.tileIcon {
  display: block;
  width: var(--sz-report-glyph-md);
  height: var(--sz-report-glyph-md);
}

/* „+ Dodaj pytanie". A dashed 1.5px frame at radius 15 with 13.5/700 inside, which is the canon's
 * geometry — and PRIMARY ink, which is not. The canon writes the word in ember; the owner overruled
 * it on 17 September 2026. The accent is a scalpel: the editor already spends it on the weekday
 * chip, on every enabled question's switch and on the mapping arrow, and a fourth ember run on the
 * one control that opens a form is what turns a scalpel into a colour scheme. The dashes still say
 * „this adds something", and they are the shape rather than the hue. */
.dash {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--space-14px);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  color: var(--text);
  text-align: center;
  background: transparent;
  border: var(--border-thick) dashed var(--border-canon-edge);
  border-radius: var(--r-button);
  cursor: pointer;
}

/* The hover brightens the FRAME, not the word: the ink is already primary, so the only thing left
 * to answer the pointer with is the edge. */
.dash:hover {
  border-color: var(--border-3);
}

.dash:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.dashIcon {
  display: block;
  flex: none;
  width: var(--sz-report-glyph-lg);
  height: var(--sz-report-glyph-lg);
}

/* The weekday chip. 32 x 32 at radius 9 — and its ON state is the flame GRADIENT, which is the
 * canon's own single accent fill on the editor. */
.wd {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--sz-report-editor-day);
  height: var(--sz-report-editor-day);
  font-size: var(--type-control);
  font-weight: var(--fw-medium);
  line-height: var(--lh-none);
  color: var(--text-2);
  background: var(--bg-inset);
  border: 0;
  border-radius: var(--radius-mark);
  cursor: pointer;
}

.wdOn {
  color: var(--on-accent-grad);
  background: var(--primary-fill);

  /* White on the accent clears 3:1 only as bold text (system spec amendment, 2 November 2026). */
  font-weight: var(--fw-semibold);
}

.wd:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The canon's `.field` — an inset frame at radius 13 with the glyph INSIDE it, never beside it.
 *
 * The WIDTH and the HEIGHT are the consumer's, because the canon draws this at 48, 44, 40 and 36
 * on four surfaces and at three widths. Declaring either here and again at the surface would put
 * one property on both sides of a chunk-order race — see the header. */
.field {
  display: flex;
  box-sizing: border-box;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-0) var(--space-3);
  font-family: var(--font-body);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-canon-edge);
  border-radius: var(--radius-13px);
}

.fieldIcon {
  display: block;
  flex: none;
  width: var(--sz-report-glyph-sm);
  height: var(--sz-report-glyph-sm);
  color: var(--text-2);
}

/* ── The two buttons: GEOMETRY only ────────────────────────────────────────────────────────── */

/* The canon's `.ink` cut is no longer transcribed. Its one wearer was the editor's „Zapisz zmiany",
 * which became the plan builder's 34px floppy square on 17 September 2026 (owner) and now reads
 * `shared/ui/saveButton.module.css`. Nothing else in this slice draws an ink primary, and the
 * export still holds the cut for the day one does.
 *
 * Worn beside `shared/ui/emberButton.module.css`. r15 / h40 / 0 16 / 13.5 / 700. */
.ember {
  gap: var(--space-2);
  height: var(--h-report-editor-field);
  padding: var(--space-0) var(--space-4);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  letter-spacing: normal;
  border-radius: var(--r-button);
}

/* Worn beside `shared/ui/ghostButton.module.css`. r15 / h40 / 0 14 / 13.5 / 600.
 *
 * A surface drawing the ghost at a DIFFERENT cut — the editor's 32px „Ukryj podgląd" — declares
 * the whole six itself and does NOT wear this class. Two stylesheets both stating `height` is the
 * chunk-order race this file exists to stop, and „most of the geometry from here plus an override"
 * is exactly that race wearing a helpful face. */
.ghost {
  gap: var(--space-2);
  height: var(--h-report-editor-field);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-semibold);
  letter-spacing: normal;
  background: transparent;
  border-radius: var(--r-button);
}

/* The canon's ghost is `color:#fff`, where the shared skin rests at `--text-2`.
 *
 * An ELEMENT selector rather than a second class, and deliberately: `color` is the skin's to
 * declare, two single-class selectors in two chunks are settled by the order the stylesheets land
 * in the document, and that order differs between `npm run dev` and `vite build`. `button.ghostInk`
 * is (0,1,1) and outranks `.ghost` (0,1,0) whichever chunk lands first. */
button.ghostInk,
a.ghostInk {
  color: var(--text);
}

/* ── Two type steps the canon states and this app had drifted from ─────────────────────────── */

/* 10.5 / 700 / .14em uppercase in `--text-3`. It was written out four times, identically, in
 * `ReportsScreen`, `SurveyColumn`, `SurveyDetailCard` and `SurveyEditor`. */
.eyebrow {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-eyebrow);
  color: var(--text-3);
  text-transform: uppercase;
}

/* 28 / 800 / -.03em. `shared/ui/screenHeader.module.css` draws the ROSTER's 31px title, which is
 * its own canon and stays; this page's export writes 28 and the eyebrow above it is what the
 * roster does not have. */
.h1 {
  font-size: var(--fs-h1);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  letter-spacing: var(--ls-display);
  color: var(--text);
}

/* ── The „wymagane" mark ───────────────────────────────────────────────────────────────────── */

/* A 16px lock glyph in the tertiary ink. No border, no fill, no accent — the owner's 17 September
 * decision, which overrides the export's own 20px ember-edged square. `RequiredMark` carries the
 * reasoning and the README records the decision. */
